import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, CardHeader, CardTitle, Input, Select, StatusChip } from '@monigo/ui';
import { callFunction } from '../../lib/api/client.js';
import { LoadingScreen } from '../../components/Status/LoadingScreen.js';
import { ErrorState } from '../../components/Status/ErrorState.js';

const NEXT_STATUSES = [
  'PAYMENT_RECEIVED',
  'PROCESSING',
  'PAID_OUT',
  'COMPLETED',
  'CANCELLED',
  'FAILED',
  'REFUNDED',
] as const;

interface Transfer {
  id: string;
  reference_code: string;
  user_id: string;
  beneficiary_id: string;
  from_currency: string;
  to_currency: string;
  amount_send: number;
  amount_receive: number;
  exchange_rate: number;
  fee: number;
  status: string;
  payment_instructions_jsonb: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

interface Sender {
  id: string;
  first_name: string | null;
  last_name: string | null;
  dob: string | null;
  address: string | null;
  country: string | null;
  language: string | null;
  status: string | null;
  has_completed_kyc: boolean;
  created_at: string | null;
  email: string | null;
  phone: string | null;
  admin_role: string | null;
  has_profile: boolean;
}

interface Beneficiary {
  id: string;
  full_name: string;
  country: string;
  payout_method: 'mobile_money' | 'bank';
  payout_details: Record<string, unknown> | null;
}

interface Event {
  id: string;
  from_status: string | null;
  to_status: string;
  note: string | null;
  actor_type: string;
  created_at: string;
}

interface DetailResp {
  transfer: Transfer;
  sender: Sender | null;
  beneficiary: Beneficiary | null;
  events: Event[];
}

export function TransferDetailPage() {
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const dateLocale = i18n.language === 'fr' ? 'fr-FR' : 'en-US';

  const detailQ = useQuery({
    queryKey: id ? ['admin-transfer-detail', id] : ['noop'],
    queryFn: () => callFunction<DetailResp>('admin-transfers-get', { query: { id: id! } }),
    enabled: !!id,
  });

  const [next, setNext] = useState<string>('');
  const [note, setNote] = useState('');

  const transition = useMutation({
    mutationFn: () =>
      callFunction('admin-transfers-transition', {
        body: { transfer_id: id, to_status: next, note: note || undefined },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-transfer-detail', id] });
      setNote('');
    },
  });

  const addNote = useMutation({
    mutationFn: () => callFunction('admin-transfers-add-note', { body: { transfer_id: id, note } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-transfer-detail', id] });
      setNote('');
    },
  });

  if (detailQ.isLoading) return <LoadingScreen />;
  if (detailQ.isError || !detailQ.data) return <ErrorState onRetry={() => detailQ.refetch()} />;
  const { transfer, sender, beneficiary, events } = detailQ.data;
  const senderName = [sender?.first_name, sender?.last_name].filter(Boolean).join(' ').trim();
  const senderFullName = senderName || sender?.email || t('transfers.unknownCustomer');
  const recipientFullName = beneficiary?.full_name ?? t('transfers.noBeneficiary');

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader>
          <CardTitle>{t('transfers.detailTitle', { ref: transfer.reference_code })}</CardTitle>
        </CardHeader>
        <div className="flex flex-wrap items-center gap-3 px-5 pb-5 text-sm">
          <StatusChip status={transfer.status} />
          <span className="text-text-muted">
            {new Date(transfer.created_at).toLocaleString(dateLocale)}
          </span>
        </div>
      </Card>

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>{t('transfers.senderTitle')}</CardTitle>
          </CardHeader>
          <dl className="grid grid-cols-3 gap-y-2 px-5 pb-5 text-sm">
            <Row label={t('customers.name')}>
              <div className="flex flex-wrap items-center gap-2">
                {sender?.has_profile ? (
                  <Link to={`/customers/${sender.id}`} className="text-primary hover:underline">
                    {senderFullName}
                  </Link>
                ) : (
                  <span>{senderFullName}</span>
                )}
                {sender?.admin_role ? (
                  <span className="inline-flex items-center rounded-pill bg-primary/15 px-2 py-0.5 text-xs font-semibold uppercase tracking-wider text-primary">
                    {sender.admin_role}
                  </span>
                ) : null}
                {sender && !sender.has_profile ? (
                  <span className="inline-flex items-center rounded-pill border border-border bg-surface-elevated px-2 py-0.5 text-xs font-medium uppercase tracking-wider text-text-muted">
                    {t('transfers.noProfileTag')}
                  </span>
                ) : null}
              </div>
            </Row>
            {sender?.email ? <Row label={t('transfers.email')}>{sender.email}</Row> : null}
            {sender?.phone ? <Row label={t('transfers.phoneNumber')}>{sender.phone}</Row> : null}
            {sender?.country ? <Row label={t('transfers.country')}>{sender.country}</Row> : null}
            {sender?.address ? <Row label={t('transfers.address')}>{sender.address}</Row> : null}
            {sender?.dob ? (
              <Row label={t('transfers.dob')}>
                {new Date(sender.dob).toLocaleDateString(dateLocale)}
              </Row>
            ) : null}
            {sender?.has_profile ? (
              <Row label={t('transfers.kycCompleted')}>
                {sender.has_completed_kyc ? t('common.yes') : t('common.no')}
              </Row>
            ) : null}
            {sender?.status ? <Row label={t('staff.status')}>{sender.status}</Row> : null}
            {sender?.language ? (
              <Row label={t('transfers.language')}>{sender.language.toUpperCase()}</Row>
            ) : null}
          </dl>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('transfers.recipientTitle')}</CardTitle>
          </CardHeader>
          <dl className="grid grid-cols-3 gap-y-2 px-5 pb-5 text-sm">
            <Row label={t('customers.name')}>{recipientFullName}</Row>
            {beneficiary?.country ? (
              <Row label={t('transfers.country')}>{beneficiary.country}</Row>
            ) : null}
            {beneficiary ? (
              <Row label={t('transfers.payoutMethod')}>
                {t(`transfers.method_label.${beneficiary.payout_method}`)}
              </Row>
            ) : null}
            {beneficiary?.payout_details
              ? renderPayoutDetails(t, beneficiary.payout_method, beneficiary.payout_details)
              : null}
          </dl>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t('transfers.summaryTitle')}</CardTitle>
        </CardHeader>
        <dl className="grid grid-cols-3 gap-y-2 px-5 pb-5 text-sm">
          <Row label={t('transfers.ref')}>
            <span className="font-mono">{transfer.reference_code}</span>
          </Row>
          <Row label={t('transfers.totalSent')}>
            <span className="font-mono">
              {transfer.amount_send} {transfer.from_currency}
            </span>
          </Row>
          <Row label={t('transfers.totalReceived')}>
            <span className="font-mono">
              {transfer.amount_receive} {transfer.to_currency}
            </span>
          </Row>
          <Row label={t('transfers.exchangeRate')}>
            <span className="font-mono">
              1 {transfer.from_currency} = {transfer.exchange_rate} {transfer.to_currency}
            </span>
          </Row>
          <Row label={t('transfers.fee')}>
            <span className="font-mono">
              {transfer.fee} {transfer.from_currency}
            </span>
          </Row>
          <Row label={t('transfers.status')}>
            <StatusChip status={transfer.status} />
          </Row>
        </dl>
      </Card>

      {transfer.payment_instructions_jsonb ? (
        <Card>
          <CardHeader>
            <CardTitle>{t('transfers.paymentInstructions')}</CardTitle>
          </CardHeader>
          <pre className="overflow-x-auto px-5 pb-5 font-mono text-xs text-text-muted">
            {JSON.stringify(transfer.payment_instructions_jsonb, null, 2)}
          </pre>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>{t('transfers.transitionTo')}</CardTitle>
        </CardHeader>
        <div className="flex flex-col gap-2 px-5 pb-5 md:flex-row">
          <Select
            value={next}
            onChange={(v) => setNext(v)}
            placeholder={t('common.all')}
            options={NEXT_STATUSES.map((s) => ({
              value: s,
              label: t(`transfers.status_label.${s}`),
            }))}
          />
          <Input
            placeholder={t('transfers.note')}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <Button
            onClick={() => transition.mutate()}
            isLoading={transition.isPending}
            disabled={!next}
          >
            {t('transfers.applyTransition')}
          </Button>
          <Button variant="ghost" onClick={() => addNote.mutate()} disabled={!note.trim()}>
            {t('transfers.addNote')}
          </Button>
        </div>
        {transition.isError ? (
          <p className="px-5 pb-5 text-sm text-danger">{t('errors.generic')}</p>
        ) : null}
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('transfers.lifecycle')}</CardTitle>
        </CardHeader>
        <ol className="flex flex-col gap-2 px-5 pb-5 text-sm">
          {events.map((ev) => (
            <li key={ev.id} className="border-l-2 border-border pl-3">
              <div className="font-semibold">{t(`transfers.status_label.${ev.to_status}`)}</div>
              <div className="text-xs text-text-muted">
                {new Date(ev.created_at).toLocaleString(dateLocale)} · {ev.actor_type}
                {ev.note ? ` · ${ev.note}` : ''}
              </div>
            </li>
          ))}
        </ol>
      </Card>
    </div>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="col-span-1 text-text-muted">{label}</dt>
      <dd className="col-span-2">{children}</dd>
    </>
  );
}

function renderPayoutDetails(
  t: (k: string) => string,
  method: 'mobile_money' | 'bank',
  details: Record<string, unknown>,
) {
  if (method === 'bank') {
    return (
      <>
        {details.iban ? (
          <Row label={t('transfers.iban')}>
            <span className="font-mono">{String(details.iban)}</span>
          </Row>
        ) : null}
        {details.bank_name ? (
          <Row label={t('transfers.bankName')}>{String(details.bank_name)}</Row>
        ) : null}
        {details.account_holder ? (
          <Row label={t('transfers.accountHolder')}>{String(details.account_holder)}</Row>
        ) : null}
      </>
    );
  }
  return (
    <>
      {details.phone_number ? (
        <Row label={t('transfers.phoneNumber')}>
          <span className="font-mono">{String(details.phone_number)}</span>
        </Row>
      ) : null}
      {details.account_holder ? (
        <Row label={t('transfers.accountHolder')}>{String(details.account_holder)}</Row>
      ) : null}
    </>
  );
}
